import React, { useState, useEffect, useRef } from "react";
import { useSpring, animated, a } from "@react-spring/web";
import {
  Box,
  IconButton,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
  Card,
  alpha,
} from "@mui/material";
import { MainCardWithTiltedCards } from "./CarouselTombs";

import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import {
  ArrowBackIosNewOutlined,
  ArrowForwardIosOutlined,
} from "@mui/icons-material";

const CustomComponentOne: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <>
      <Grid container spacing={1}>
        <Grid item xs={12} sm={4} md={4}>
          <Box
            sx={{
              //width: "100%", // 50% of the parent Box
              //minHeight: "500px",
              //marginBottom: 6,
              paddingLeft: 3,
              paddingTop: 3,
              zIndex: 15,
            }}
          >
            <Typography
              variant={isMobile ? "h6" : "h5"}
              component="div"
              gutterBottom
            >
              Add-ins
            </Typography>
            <Typography
              variant={isMobile ? "body1" : "h5"}
              sx={{
                color: "text.secondary",
              }} // Adjust the styling as needed
              component="div"
              gutterBottom
            >
              One-click access and reuse of your data
              <br />
              <br />
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={8} md={8}>
          <Box
            sx={{
              width: "100%", // 50% of the parent Box
              height: "100%",
              zIndex: 10,
            }}
          >
            <img
              src="addins.png"
              alt="PowerPoint add-in"
              style={{
                width: "100%",
                height: "auto",
                display: "block",
              }}
            />
          </Box>
        </Grid>
      </Grid>
    </>
  );
};

const CustomComponentPipeline: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <>
      <Grid container spacing={1}>
        <Grid item xs={12} sm={4} md={4}>
          <Box
            sx={{
              //width: "100%", // 50% of the parent Box
              //minHeight: "500px",
              //marginBottom: 6,
              paddingLeft: 3,
              paddingTop: 3,
              zIndex: 9,
            }}
          >
            <Typography
              variant={isMobile ? "h6" : "h5"}
              component="div"
              gutterBottom
            >
              Dashboards & reporting
            </Typography>
            <Typography
              variant={isMobile ? "body1" : "h5"}
              sx={{
                color: "text.secondary",
              }} // Adjust the styling as needed
              component="div"
              gutterBottom
            >
              Easy publish to dashboards Effortlessly generate reports <br />
              <br />
              <br />
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={8} md={8}>
          <Box
            sx={{
              width: "100%", // 50% of the parent Box
              height: "100%",
              zIndex: 10,
            }}
          >
            <img
              src="pipeline2.png"
              alt="PowerPoint add-in"
              style={{
                width: "100%",
                height: "auto",
                display: "block",
              }}
            />
          </Box>
        </Grid>
      </Grid>
    </>
  );
};

// Example of a custom React component to include in a slide
const CustomComponent: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <>
      <Grid container spacing={1}>
        <Grid item xs={12} sm={4} md={4}>
          <Box
            sx={{
              //width: "100%", // 50% of the parent Box
              //minHeight: "500px",
              //marginBottom: 6,
              paddingLeft: 3,
              paddingTop: 3,
              //zIndex: 9,
            }}
          >
            <Typography
              variant={isMobile ? "h6" : "h5"}
              component="div"
              gutterBottom
            >
              From idea to completed project
            </Typography>
            <Typography
              variant={isMobile ? "body1" : "h5"}
              sx={{
                color: "text.secondary",
              }} // Adjust the styling as needed
              component="div"
              gutterBottom
            >
              Get overview
              <br />
              Reuse data and documents <br />
              Save time and resources
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={8} md={8}>
          <Box
            sx={{
              width: "100%", // 50% of the parent Box
              height: "100%",
              zIndex: 10,
            }}
          >
            <MainCardWithTiltedCards />
          </Box>
        </Grid>
      </Grid>
    </>
  );
};

interface SlideProps {
  title: string;
  description: string;
  imageUrl?: string; // Optional image URL for slides with images
  Component?: React.FC; // Optional React component for slides with custom components
}

const slides: SlideProps[] = [
  {
    title: "Tombstones",
    description: "This is the second slide.",
    Component: CustomComponent,
  },
  {
    title: "Add-ins",
    description: "This is the first slide.",
    Component: CustomComponentOne,
  },

  {
    title: "Pipeline",
    description: "This is the first slide.",
    Component: CustomComponentPipeline,
  },
];

const Carousel: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [index, setIndex] = useState(0);
  const autoSlideRef = useRef<NodeJS.Timeout | null>(null);

  const { transform } = useSpring({
    to: { transform: `translate3d(-${index * 100}%, 0, 0)` },
    from: { transform: "translate3d(0%, 0, 0)" },
    config: { mass: 1, tension: 280, friction: 60 },
  });

  const startAutoSlide = () => {
    stopAutoSlide(); // Clear existing timer if any
    autoSlideRef.current = setInterval(() => {
      setIndex((prevIndex) => (prevIndex + 1) % slides.length);
    }, 5000); // Adjust the timing as needed
  };

  const stopAutoSlide = () => {
    if (autoSlideRef.current) {
      clearInterval(autoSlideRef.current);
      autoSlideRef.current = null;
    }
  };

  useEffect(() => {
    startAutoSlide();
    return () => stopAutoSlide(); // Cleanup on component unmount
  }, []);

  const handleMouseEnter = () => {
    stopAutoSlide();
  };

  const handleMouseLeave = () => {
    startAutoSlide();
  };

  const nextSlide = () => {
    stopAutoSlide(); // Stop the auto-slide when manually navigating
    setIndex((prevState) => (prevState + 1) % slides.length);
  };

  const prevSlide = () => {
    stopAutoSlide(); // Stop the auto-slide when manually navigating
    setIndex((prevState) => (prevState - 1 + slides.length) % slides.length);
  };

  return (
    <Box
      sx={{
        //height: "50vh",
        mt: 2,
        px: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          position: "relative",
        }}
      >
        {/* Navigation Buttons */}
        <IconButton
          onClick={prevSlide}
          sx={{
            position: "absolute",
            left: -30,

            //transform: "translate(-100%, -50%)",
            zIndex: 1,
            color: "text.secondary",
            transition:
              "color 0.3s ease-in-out, background-color 0.3s ease-in-out", // Added transition for background-color
            backgroundColor: alpha(theme.palette.common.white, 0.0), // Use theme's white color with alpha for transparency
            "&:hover": {
              color: "#fff", // Change this color as needed
              backgroundColor: alpha(theme.palette.common.white, 0.05), // Adjust opacity on hover
            },
          }}
          size={isMobile ? "small" : "large"}
        >
          <ArrowBackIosNewOutlined />
        </IconButton>
        <Card
          variant="outlined"
          sx={{
            maxWidth: 1300,
            maxHeight: 500,
            width: "95%",
            //marginBottom: 2,
            backgroundColor: "#12181f", // Custom background color
            border: "1px solid #30363d", // Custom border color
            color: "text.primary", // Ensures text color is from the theme
            position: "relative",
          }}
        >
          <Box
            sx={{ width: "100%", overflow: "hidden", position: "relative" }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <animated.div
              style={{
                display: "flex",
                willChange: "transform",
                transform,
              }}
            >
              {slides.map((slide, i) => (
                <Box
                  key={i}
                  sx={{
                    flexShrink: 0,
                    width: "100%",
                    height: "100%",
                    p: 0,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    justifyContent: "center",
                  }}
                >
                  {slide.Component && <slide.Component />}
                </Box>
              ))}
            </animated.div>
          </Box>
        </Card>{" "}
        <IconButton
          onClick={nextSlide}
          sx={{
            position: "absolute",
            right: -30,
            //transform: "translate(100%, -50%)",
            zIndex: 1,
            color: "text.secondary",
            transition:
              "color 0.3s ease-in-out, background-color 0.3s ease-in-out", // Added transition for background-color
            backgroundColor: alpha(theme.palette.common.white, 0.0), // Use theme's white color with alpha for transparency
            "&:hover": {
              color: "#fff", // Change this color as needed
              backgroundColor: alpha(theme.palette.common.white, 0.05), // Adjust opacity on hover
            },
          }}
          size={isMobile ? "small" : "large"}
        >
          <ArrowForwardIosOutlined />
        </IconButton>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
        {slides.map((_, i) => (
          <Box
            key={i}
            onClick={() => {
              stopAutoSlide(); // Stop auto-slide when a dot is clicked
              setIndex(i); // Set the carousel to the clicked slide
            }}
            sx={{
              width: isMobile ? 9 : 12,
              height: isMobile ? 9 : 12,
              borderRadius: "50%",
              backgroundColor: index === i ? "#007fff" : "grey",
              margin: "0 4px",
              cursor: "pointer",
              "&:hover": {
                backgroundColor:
                  index === i
                    ? "#007fff"
                    : alpha(theme.palette.common.white, 0.75),
              },
              transition: "background-color 0.3s",
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export default Carousel;
